import { useCallback } from 'react';

import { UpdateCommentInput } from '@this+that/models';
import log from 'loglevel';

import { useInboxEntryCommentsSlice } from './useInboxEntryCommentsSlice';
import {
  MutationResult,
  useUpdateComment,
} from '../../services/inboxEntryComments.service';

export const useUpdateInboxEntryComment = (inboxEntryId: string) => {
  const { updateComment: updateCommentService } = useUpdateComment();

  const {
    updatingCommentId,
    editingCommentId,
    setUpdatingCommentId,
    setEditingCommentId,
    updateComment,
    setError,
  } = useInboxEntryCommentsSlice(inboxEntryId);

  const handleUpdateComment = useCallback(
    async (
      input: Omit<UpdateCommentInput, 'inboxEntryId'>,
    ): Promise<MutationResult<null>> => {
      setUpdatingCommentId(input.commentId);
      setError(null);

      try {
        const { data, errors } = await updateCommentService({
          ...input,
          inboxEntryId,
        });

        if (errors?.length) {
          log.error(
            `Error updating comment ${input.commentId} for inbox entry ${inboxEntryId}:`,
            errors,
          );
          return { data: null, errors };
        }

        if (data) {
          updateComment(data);
          setEditingCommentId(null);
        }

        return { data: null };
      } catch (error) {
        log.error(
          `Error updating comment ${input.commentId} for inbox entry ${inboxEntryId}:`,
          error,
        );
        const message =
          error instanceof Error ? error.message : 'Unknown error';
        return { data: null, errors: [{ message }] };
      } finally {
        setUpdatingCommentId(null);
      }
    },
    [
      inboxEntryId,
      updateCommentService,
      updateComment,
      setUpdatingCommentId,
      setEditingCommentId,
      setError,
    ],
  );

  return {
    handleUpdateComment,
    updatingCommentId,
    editingCommentId,
    setEditingCommentId,
  };
};
